import { useState, useEffect } from "react";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";

type AppImages = {
  url_128x128: string;
  url_64x64: string;
  url_32x32: string;
  url_16x16: string;
};

type PreTransformedApp = {
  images: AppImages;
  id: string;
  canonical_id: string;
  current_implementation_id: string;
  name: string;
  slug: string;
};

export type App = {
  id: string | number;
  canonicalId: string;
  serviceId?: string;
  slug: string;
  name: string;
  images: AppImages;
  currentImplementation: {
    id: string;
  };
};

type Options = {
  limit?: number;
  searchDebounceDelay?: number;
  excludeUpcoming?: boolean;
  includeIsAppAdmin?: boolean;
  includeIsAppCollaborator?: boolean;
  apiHostname?: string;
  onResultsUpdate?: (searchQuery: string, results: any) => void;
};

const DEFAULT_LIMIT = 20;

const dedupeAndTrimWhitespace = (query: string) =>
  query.replace(/^\s+|\s+$/g, "");

/**
 * Provides the ability to search for Zapier apps (services, integrations)
 * and receive a set of matching results.
 */
export const useAppSearchData = ({
  limit,
  searchDebounceDelay = 300,
  excludeUpcoming,
  includeIsAppAdmin,
  includeIsAppCollaborator,
  apiHostname,
  onResultsUpdate,
}: Options) => {
  const [searchQuery, setSearchQuery] = useState<string>("");

  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    searchDebounceDelay,
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<App[]>([]);

  useEffect(() => {
    const searchQueryTrimmed = dedupeAndTrimWhitespace(debouncedSearchQuery);
    if (searchQueryTrimmed === "") {
      setData([]);
      return;
    }

    const fetchAppData = async () => {
      const searchParams = new URLSearchParams();
      searchParams.append("limit", (limit || DEFAULT_LIMIT).toString());
      searchParams.append("title_search", searchQueryTrimmed);
      if (excludeUpcoming)
        searchParams.append("exclude_upcoming", excludeUpcoming.toString());
      if (includeIsAppAdmin)
        searchParams.append(
          "including_is_app_admin",
          includeIsAppAdmin.toString(),
        );
      if (includeIsAppCollaborator)
        searchParams.append(
          "including_is_app_collaborator",
          includeIsAppCollaborator.toString(),
        );

      try {
        setLoading(true);
        const response = await fetch(
          `${apiHostname}/api/v4/apps?${searchParams.toString()}`,
        );
        if (!response.ok) {
          throw new Error("Response was not OK");
        }
        const preTransformedAppsResponse = await response.json();
        const edges = preTransformedAppsResponse.results.map(
          (app: PreTransformedApp) => ({
            id: app.id,
            canonicalId: app.canonical_id,
            slug: app.slug,
            name: app.name,
            currentImplementation: { id: app.current_implementation_id },
            images: app.images,
          }),
        );

        setData(edges);
        if (onResultsUpdate) {
          onResultsUpdate(searchQueryTrimmed, edges);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppData();
  }, [
    debouncedSearchQuery,
    apiHostname,
    limit,
    excludeUpcoming,
    includeIsAppAdmin,
    includeIsAppCollaborator,
    onResultsUpdate,
  ]);

  return {
    loading,
    onSearch: setSearchQuery,
    searchQuery,
    apps: data,
    setSearchQuery,
  };
};
